import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {CustomProject, CustomProjectStore} from "../domain/custom.model";
import api from "@src/common/services/api.service";
import AppConstants from "@src/utils/app.constants";
import {ApplicationResponse} from "@src/common/domain/common.model";

export const validateProjectName = createAsyncThunk<
    boolean,
    string,
    { rejectValue: string }
>(
    "customProject/validateProjectName",
    async (projectName, {rejectWithValue}) => {
        const url = `${AppConstants.api}/custom/validate-name`;
        try {
            const response = await api.get<ApplicationResponse<boolean>>(url, {
                params: {projectName},
            });
            if (response.data.data === true) {
                return true;
            }
            return rejectWithValue('Project with same "Project Name" already exists');
        } catch (err: any) {
            const message = err.response?.data?.message || err.message;
            return rejectWithValue(message);
        }
    }
);

export const addCustomProject = createAsyncThunk<any, { payload: any }>(
    "customProject/addCustomProject",
    async ({payload}) => {
        const url = `${AppConstants.api}/custom/create`;
        const response = await api.post<ApplicationResponse<any>>(url, payload, {
            params: {
                RAR: 0,
            },
        });
        return response.data.data || {};
    }
);

export const updateCustomProject = createAsyncThunk<
    void,
    { id: string; data: any }
>("customProject/updateCustomProject", async ({id, data}) => {
    const url = `${AppConstants.api}/custom/update`;
    try {
        const response = await api.put<ApplicationResponse<any>>(url, data, {
            params: {
                id: id,
                RAR: 0,
            },
        });
        return response.data.data || {};
    } catch (error) {
        // return rejectWithValue(error.response?.data || error.message);
    }
});

export const fetchApproverList = createAsyncThunk<any[], { id: string }>(
    "approver/fetchApprover",
    async ({id}) => {
        const url = `${AppConstants.api}/custom/approver`;

        const response = await api.get<ApplicationResponse<any[]>>(url, {
            params: {
                id,
            },
        });
        return response.data.data || [];
    }
);

export const fetchCheckerList = createAsyncThunk<any[], { id: string }>(
    "checker/fetchChecker",
    async ({id}) => {
        const url = `${AppConstants.api}/custom/checker`;

        const response = await api.get<ApplicationResponse<any[]>>(url, {
            params: {
                id,
            },
        });
        return response.data.data || [];
    }
);

export const fetchCustomTasksList = createAsyncThunk<any[], { id: string }>(
    "customTasks/fetchCustomTasks",
    async ({id}) => {
        const url = `${AppConstants.api}/custom/tasks`;

        const response = await api.get<ApplicationResponse<any[]>>(url, {
            params: {
                id,
            },
        });
        return response.data.data || [];
    }
);

export const getCustomUserRole = createAsyncThunk<
    any,
    { userId: string; projectId: string }
>("custom/get-custom-user-role", async ({userId, projectId}) => {

    const url = `${AppConstants.api}/custom/user-role`;
    const response = await api.get<ApplicationResponse<any>>(url, {
        params: {
            userId,
            projectId,
        },
    });
    return response.data.data || [];
});

export const getCustomApprovalInfo = createAsyncThunk<
    any,
    { projectId: string }
>("custom/get-custom-approval-flow", async ({projectId}) => {
    const url = `${AppConstants.api}/custom/${projectId}/approval-flow`;
    const response = await api.get<ApplicationResponse<any>>(url);
    return response.data.data || {};
});

export const getApprovalInfo = createAsyncThunk<
    any,
    { projectId: string; projectType: string }
>("approval/get-detail", async ({projectId, projectType}) => {
    const url = `${AppConstants.api}/approval/detail`;
    const response = await api.get<ApplicationResponse<any>>(url, {
        params: {
            projectId,
            type: projectType.toUpperCase(),
        },
    });
    return response.data.data || {};
});

export const getApprovedByApproverByOrder = createAsyncThunk<
    any,
    { projectId: string, approvalId: string, userId: string }
>("custom/get-approved-by-approver", async ({projectId, approvalId, userId}) => {
    const url = `${AppConstants.api}/custom/${projectId}/approval-flow/approve/${approvalId}?userId=${userId}`;
    const response = await api.post<ApplicationResponse<any>>(url);
    return;
});

export const rejectByApproverByOrder = createAsyncThunk<
    any,
    { projectId: string, approvalId: string, userId: string, remarks: string }
>("custom/get-rejected-by-approver", async ({projectId, approvalId, userId, remarks}) => {
    const url = `${AppConstants.api}/custom/${projectId}/approval-flow/reject/${approvalId}?userId=${userId}&remarks=${remarks}`;
    const response = await api.post<ApplicationResponse<any>>(url);
    return;
});

const customProjectSlice = createSlice({
    name: "customProject",
    initialState: {
        minimal: {status: "loading", error: null, data: []},
        approverMinimal: {status: "loading", error: null, data: []},
        checkerMinimal: {status: "loading", error: null, data: []},
        customMinimal: {status: "loading", error: null, data: []},
        appovalInfo: {status: "loading", error: null, data: {}},
        validateName: {status: "loading", error: null, data: []},

    } as CustomProjectStore,
    reducers: {
        resetTasks: (state) => {
            state.customMinimal = {
                status: "loading",
                error: null,
                data: [],
            };
        },
        resetApprover: (state) => {
            state.approverMinimal = {
                status: "loading",
                error: null,
                data: [],
            };
        },
        resetChecker: (state) => {
            state.checkerMinimal = {
                status: "loading",
                error: null,
                data: [],
            };
        },
        updateAddApprover: (state, action) => {
            state.minimal.data.push(action.payload);
        },
        updateAddTask: (state, action) => {
            state.minimal.data.push(action.payload);
        },

        removeChecker: (state, action) => {
            state.minimal.data = state.minimal.data.filter(
                (user: { id: any }) => user.id !== action.payload
            );
        },
        removeTask: (state, action) => {
            state.minimal.data = state.minimal.data.filter(
                (task: { id: any }) => task.id !== action.payload
            );
        },
        editTask: (state, action) => {
            const {id, updatedTask} = action.payload;
            const index = state.minimal.data.findIndex(
                (task: { id: any }) => task.id === id
            );
            if (index !== -1) {
                state.minimal.data[index] = {
                    ...state.minimal.data[index],
                    ...updatedTask,
                };
            }
        },
        removeApprover: (state, action) => {
            state.minimal.data = state.minimal.data.filter(
                (approver: { id: any }) => approver.id !== action.payload
            );
        },
        removeUser: (state, action) => {
            state.minimal.data = state.minimal.data.filter(
                (user: { id: any }) => user.id !== action.payload
            );
        },
        resetCustomUserRole: (state) => {
            state.customUserRole = {
                status: "loading",
                error: null,
                data: [],
            };

        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCheckerList.pending, (state, action) => {
                state.checkerMinimal = {
                    status: "loading",
                    data: [],
                    error: null,
                };
            })
            .addCase(fetchCheckerList.fulfilled, (state, action) => {
                state.checkerMinimal = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(fetchCheckerList.rejected, (state, action) => {
                state.checkerMinimal = {
                    status: "failed",
                    data: [],
                    error: action.error,
                };
            })
            .addCase(fetchApproverList.pending, (state, action) => {
                state.approverMinimal = {
                    status: "loading",
                    data: [],
                    error: null,
                };
            })
            .addCase(fetchApproverList.fulfilled, (state, action) => {
                state.approverMinimal = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(fetchApproverList.rejected, (state, action) => {
                state.approverMinimal = {
                    status: "failed",
                    data: [],
                    error: action.error,
                };
            })
            .addCase(fetchCustomTasksList.pending, (state) => {
                state.customMinimal = {
                    status: "loading",
                    data: [],
                    error: null,
                };
            })
            .addCase(fetchCustomTasksList.fulfilled, (state, action) => {
                state.customMinimal = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(fetchCustomTasksList.rejected, (state, action) => {
                state.customMinimal = {
                    status: "failed",
                    data: [],
                    error: action.error,
                };
            });
        builder
            .addCase(getCustomUserRole.pending, (state, action) => {
                state.customUserRole = {
                    status: "loading",
                    data: [],
                    error: null,
                };
            })
            .addCase(getCustomUserRole.fulfilled, (state, action) => {
                state.customUserRole = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(getCustomUserRole.rejected, (state, action) => {
                state.customUserRole = {
                    status: "failed",
                    data: [],
                    error: action.error,
                };
            });
        builder
            .addCase(getCustomApprovalInfo.pending, (state, action) => {
                state.appovalInfo = {
                    status: "loading",
                    data: {},
                    error: null,
                };
            })
            .addCase(getCustomApprovalInfo.fulfilled, (state, action) => {
                state.appovalInfo = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(getCustomApprovalInfo.rejected, (state, action) => {
                state.appovalInfo = {
                    status: "failed",
                    data: {},
                    error: action.error,
                };
            });
        builder
            .addCase(validateProjectName.pending, (state) => {
                state.validateName = {status: "loading", error: null, data: []};
            })
            .addCase(validateProjectName.fulfilled, (state) => {
                state.validateName = {status: "idle", error: null, data: []};
            })
            .addCase(validateProjectName.rejected, (state, action) => {
                state.validateName = {status: "failed", error: action.payload as string, data: []};
            });
        builder
            .addCase(getApprovalInfo.pending, (state, action) => {
                state.appovalInfo = {
                    status: "loading",
                    data: {},
                    error: null,
                };
            })
            .addCase(getApprovalInfo.fulfilled, (state, action) => {
                state.appovalInfo = {
                    status: "idle",
                    data: action.payload,
                    error: null,
                };
            })
            .addCase(getApprovalInfo.rejected, (state, action) => {
                state.appovalInfo = {
                    status: "failed",
                    data: {},
                    error: action.error,
                };
            });
    },
});
export const {
    resetCustomUserRole,
    updateAddTask,
    updateAddApprover,
    removeChecker,
    removeUser,
    removeApprover,
    removeTask,
    editTask,
    resetTasks,
    resetChecker,
    resetApprover,
} = customProjectSlice.actions;

export default customProjectSlice.reducer;
